/**
 * Database sync layer — bridges localStorage stores with Supabase.
 * Uses raw supabase client with type casts since generated types may lag behind migrations.
 */

import { supabase } from "@/integrations/supabase/client";

let currentUserId: string | null = null;

export function setCurrentUserId(id: string | null) {
  currentUserId = id;
}

export function getCurrentUserId(): string | null {
  return currentUserId;
}

// Helper to bypass strict typing on dynamically-created tables
function from(table: string) {
  return (supabase as any).from(table);
}

export async function dbInsert(table: string, data: Record<string, unknown>) {
  if (!currentUserId) return null;
  try {
    const payload = { ...data, user_id: currentUserId };
    const { data: result, error } = await from(table).insert(payload).select().single();
    if (error) throw error;
    return result;
  } catch (e) {
    console.error(`[dbSync] insert ${table} failed:`, e);
    return null;
  }
}

export async function dbUpsert(table: string, data: Record<string, unknown>, conflictColumn?: string) {
  if (!currentUserId) return;
  try {
    const payload = { ...data, user_id: currentUserId };
    const opts = conflictColumn ? { onConflict: conflictColumn } : {};
    await from(table).upsert(payload, opts);
  } catch (e) {
    console.error(`[dbSync] upsert ${table} failed:`, e);
  }
}

export async function dbDelete(table: string, id: string) {
  if (!currentUserId) return;
  try {
    await from(table).delete().eq("id", id).eq("user_id", currentUserId);
  } catch (e) {
    console.error(`[dbSync] delete ${table} failed:`, e);
  }
}

export async function dbSelect<T = any>(table: string, orderBy?: string): Promise<T[]> {
  if (!currentUserId) return [];
  try {
    let query = from(table).select("*").eq("user_id", currentUserId);
    if (orderBy) query = query.order(orderBy, { ascending: false });
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as T[];
  } catch (e) {
    console.error(`[dbSync] select ${table} failed:`, e);
    return [];
  }
}

export async function dbSelectSingle<T = any>(table: string): Promise<T | null> {
  if (!currentUserId) return null;
  try {
    const { data, error } = await from(table)
      .select("*")
      .eq("user_id", currentUserId)
      .maybeSingle();
    if (error) throw error;
    return data as T | null;
  } catch (e) {
    console.error(`[dbSync] selectSingle ${table} failed:`, e);
    return null;
  }
}

export async function dbUpdate(table: string, match: Record<string, unknown>, data: Record<string, unknown>) {
  if (!currentUserId) return;
  try {
    let query = from(table).update(data).eq("user_id", currentUserId);
    for (const [key, val] of Object.entries(match)) {
      query = query.eq(key, val);
    }
    await query;
  } catch (e) {
    console.error(`[dbSync] update ${table} failed:`, e);
  }
}
